import CloseIcon from '@mui/icons-material/Close';
import { Grow, IconButton, Snackbar } from '@mui/material';
import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import CongratsPage from './pages/CongratsPage';
import CustomerJourney from './pages/CustomerJourney';
import CustomerJourney2 from './pages/CustomerJourney2';
import DashboardPage from './pages/Dashboard';
import ForbiddenErrorPage from './pages/ForbiddenErrorPage';
import InternalErrorPage from './pages/InternalErrorPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ReferralDashboardPage from './pages/ReferralDashboardPage';
import NotFoundPage from './pages/NotFoundPage';
import OpenOffersLandingPage from './pages/OpenOffersLandingPage';
import OpenOffersPage from './pages/OpenOffersPage';
import ProfilePage from './pages/ProfilePage';
import ProjectsPage from './pages/ProjectsPage';
import VendorProfileList from './pages/VendorProfileList';
import VendorProfilePage from './pages/VendorProfilePage';
import VerifiedReviewsPage from './pages/VerifiedReviewsPage';
import WriteNowPage from './pages/WriteNowPage';
import WriteReviewPage from './pages/WriteReviewPage';
import RouteGuard from './services/RouteGuard';
import { useSearchAnimate } from './store/SearchAnimateStore';
import ProposalsPage from './pages/ProposalsPage';
import RoiPage from './pages/RoiPage';
import GetQuote from './components/landing/GetQuote';
import DesignPlant from './pages/DesignPlant';
import SolarGeneration from './pages/SolarGeneration';
import ShareableApplicationPage from './pages/ShareableApplicationPage';
import SLD from './pages/SLD';

function GrowTransition(props) {
  return <Grow {...props} />;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const helmetContext = {};
  const { isAlert, setIsAlert } = useSearchAnimate();

  useEffect(() => {
    // The root URL stays at '/' - it must not be rewritten to '/en'.
    if (location.pathname === '/') {
      return;
    }
    // Extract the language code from the URL path and check if it's valid.
    const languageCode = location.pathname.split('/')[1];
    const validLanguages = ['en', 'hi', 'ta'];

    if (!validLanguages.includes(languageCode)) {
      navigate('/en' + location.pathname + location.search, { replace: true });
    }
  }, [location, navigate]);

  return (
    <HelmetProvider context={helmetContext}>
      <Routes>
        <Route path='/' element={<LandingPage />} />
        <Route path='/:lang' element={<LandingPage />} />
        <Route path='/:lang/getquote' element={<GetQuote />} />
        <Route path='/:lang/login' element={<LoginPage />} />
        <Route path='/:lang/referral' element={<ReferralDashboardPage />} />
        <Route path='/:lang/start2' element={<OpenOffersLandingPage />} />
        <Route path='/:lang/design-plants' element={<DesignPlant />} />
        <Route path='/:lang/sld' element={<SLD />} />
        <Route path='/:lang/solar-generation' element={<SolarGeneration />} />
        {/* Temp route, to be removed after campaign change, Dec 2024 */}
        <Route path='/:lang/start' element={<CustomerJourney />} />
        <Route path='/:lang/site-details' element={<CustomerJourney2 />} />
        <Route
          path='/:lang/vendors'
          element={
            <RouteGuard>
              <VendorProfileList />
            </RouteGuard>
          }
        />
        <Route
          path='/:lang/referral'
          element={
            <RouteGuard>
              <ReferralDashboardPage />
            </RouteGuard>
          }
        />
        <Route
          path='/:lang/vendor-profile/:vendorInfo'
          element={<VendorProfilePage />}
        />
        <Route
          path='/:lang/dashboard'
          element={
            <RouteGuard>
              <DashboardPage />
            </RouteGuard>
          }
        />
        <Route path='/:lang/congratulations' element={<CongratsPage />} />
        <Route
          path='/:lang/projects'
          element={
            <RouteGuard>
              <ProjectsPage />
            </RouteGuard>
          }
        />
        <Route
          path='/:lang/profile'
          element={
            <RouteGuard>
              <ProfilePage />
            </RouteGuard>
          }
        />
        <Route path='/:lang/error' exact Component={InternalErrorPage} />
        <Route path='/:lang/forbidden' exact Component={ForbiddenErrorPage} />

        <Route path='/:lang/write-review' exact element={<WriteReviewPage />} />
        <Route path='/:lang/write-now' exact element={<WriteNowPage />} />
        <Route
          path='/:lang/verify-review'
          exact
          element={<VerifiedReviewsPage />}
        />
        <Route
          path='/:lang/open-offers-view'
          exact
          element={<OpenOffersLandingPage />}
        />
        <Route path='/:lang/open-offers' exact element={<OpenOffersPage />} />
        <Route path='/:lang/proposals' element={<ProposalsPage />} />
        <Route path='*' Component={NotFoundPage} />
        <Route path='/:lang/roi' exact element={<RoiPage />} />
        <Route
          path='/:lang/shareableapplication/:encodedId'
          exact
          element={<ShareableApplicationPage />}
        />
      </Routes>
      <Snackbar
        open={isAlert.show}
        TransitionComponent={GrowTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50px !important',
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'white',
            color: 'primary.main',
          },
        }}
        autoHideDuration={1500}
        onClose={() => setIsAlert({ show: false, msg: isAlert.msg })}
        message={isAlert.msg}
        action={
          <IconButton
            size='small'
            aria-label='close'
            color='inherit'
            onClick={() => setIsAlert({ show: false, msg: isAlert.msg })}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        }
      />
    </HelmetProvider>
  );
}

export default App;
